import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { pharmacyAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Pill,
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
  X,
  Loader2,
  Eye,
  Phone,
  MessageSquare,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

// Status configuration
const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  processing: { label: 'En traitement', color: 'bg-blue-100 text-blue-800', icon: Package },
  available: { label: 'Disponible', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  partial: { label: 'Partiellement disponible', color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
  unavailable: { label: 'Non disponible', color: 'bg-red-100 text-red-800', icon: X },
  ready: { label: 'Prêt à récupérer', color: 'bg-purple-100 text-purple-800', icon: CheckCircle },
  completed: { label: 'Complété', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Annulé', color: 'bg-gray-100 text-gray-800', icon: X },
};

const PharmacyDashboard = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseData, setResponseData] = useState({
    status: '',
    pharmacy_response: '',
    total_price: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ordersRes, profileRes] = await Promise.all([
        pharmacyAPI.getPharmacyOrders(),
        pharmacyAPI.getProfile()
      ]);
      setOrders(ordersRes.data.orders || []);
      setProfile(profileRes.data.profile);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    if (filter === 'pending') return order.status === 'pending';
    if (filter === 'processing') return ['processing', 'available', 'partial', 'unavailable'].includes(order.status);
    if (filter === 'ready') return order.status === 'ready';
    if (filter === 'completed') return ['completed', 'cancelled'].includes(order.status);
    return true;
  });

  const stats = {
    pending: orders.filter(o => o.status === 'pending').length,
    processing: orders.filter(o => ['processing', 'available', 'partial', 'unavailable'].includes(o.status)).length,
    ready: orders.filter(o => o.status === 'ready').length,
    completed: orders.filter(o => o.status === 'completed').length,
  };

  const handleOpenResponse = (order, newStatus) => {
    setSelectedOrder(order);
    setResponseData({
      status: newStatus,
      pharmacy_response: '',
      total_price: ''
    });
    setShowResponseModal(true);
  };

  const handleSubmitResponse = async () => {
    if (!selectedOrder) return;
    
    setSubmitting(true);
    try {
      const data = {
        status: responseData.status,
        pharmacy_response: responseData.pharmacy_response || null,
        total_price: responseData.total_price ? parseFloat(responseData.total_price) : null
      };
      
      await pharmacyAPI.updateOrderStatus(selectedOrder.id, data);
      toast.success('Statut mis à jour');
      setShowResponseModal(false);
      setSelectedOrder(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkReady = async (orderId) => {
    try {
      await pharmacyAPI.updateOrderStatus(orderId, { status: 'ready' });
      toast.success('Commande prête à récupérer');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleCompletePayment = async (orderId) => {
    if (!window.confirm('Confirmer le paiement et compléter la commande ?')) return;
    
    try {
      await pharmacyAPI.completePayment(orderId);
      toast.success('Commande complétée !');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  // Check if pharmacy is validated
  if (profile && !profile.is_validated) {
    return (
      <div className="px-4 md:px-6 lg:px-8 py-6">
        <Card className="border-0 shadow-sm max-w-lg mx-auto mt-12">
          <CardContent className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Compte en attente de validation</h2>
            <p className="text-slate-600">
              Votre compte pharmacie est en cours de vérification par notre équipe.
              Vous recevrez une notification une fois validé.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 lg:px-8 py-6" data-testid="pharmacy-dashboard">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-green-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {profile?.pharmacy_name || 'Tableau de bord'}
        </h1>
        <p className="text-[#64748B] mt-1">
          Gérez vos commandes d&apos;ordonnances
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-0 shadow-sm bg-yellow-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
            <p className="text-sm text-yellow-700">En attente</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-blue-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{stats.processing}</p>
            <p className="text-sm text-blue-700">En traitement</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-purple-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-purple-600">{stats.ready}</p>
            <p className="text-sm text-purple-700">À récupérer</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-green-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{stats.completed}</p>
            <p className="text-sm text-green-700">Complétées</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { key: 'pending', label: 'En attente', count: stats.pending },
          { key: 'processing', label: 'En traitement', count: stats.processing },
          { key: 'ready', label: 'À récupérer', count: stats.ready },
          { key: 'completed', label: 'Terminées', count: stats.completed },
          { key: 'all', label: 'Toutes', count: orders.length },
        ].map((f) => (
          <Button
            key={f.key}
            variant={filter === f.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.key)}
            className={filter === f.key ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            {f.label} ({f.count})
          </Button>
        ))}
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="text-center py-12">
            <Package className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">Aucune commande</h3>
            <p className="text-slate-500 mt-2">
              Les commandes d&apos;ordonnances apparaîtront ici
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => {
            const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
            const StatusIcon = statusConfig.icon;
            
            return (
              <Card key={order.id} className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    {/* Prescription Image */}
                    <div className="w-24 h-24 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer"
                         onClick={() => window.open(order.prescription_image_url, '_blank')}>
                      {order.prescription_image_url && (
                        <img 
                          src={order.prescription_image_url} 
                          alt="Ordonnance"
                          className="w-full h-full object-cover"
                        />
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity">
                        <Eye className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    
                    {/* Order Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-slate-900">{order.patient_name}</h3>
                          <p className="text-sm text-slate-500">
                            {new Date(order.created_at).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <Badge className={statusConfig.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </div>
                      
                      {order.notes && (
                        <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded mb-2">
                          <MessageSquare className="w-3 h-3 inline mr-1" />
                          {order.notes}
                        </p>
                      )}
                      
                      {order.pharmacy_response && (
                        <p className="text-sm text-green-700 bg-green-50 p-2 rounded mb-2">
                          <strong>Votre réponse:</strong> {order.pharmacy_response}
                        </p>
                      )}
                      
                      {order.total_price && (
                        <p className="font-semibold text-green-600">
                          <DollarSign className="w-4 h-4 inline" />
                          {order.total_price.toLocaleString()} FCFA
                        </p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      {order.patient_phone && (
                        <a href={`tel:${order.patient_phone}`}>
                          <Button variant="outline" size="sm">
                            <Phone className="w-4 h-4" />
                          </Button>
                        </a>
                      )}
                      
                      {order.status === 'pending' && (
                        <Button 
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={() => handleOpenResponse(order, 'processing')}
                        >
                          Traiter
                        </Button>
                      )}
                      
                      {order.status === 'processing' && (
                        <>
                          <Button 
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleOpenResponse(order, 'available')}
                          >
                            Disponible
                          </Button>
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenResponse(order, 'partial')}
                          >
                            Partiel
                          </Button>
                          <Button 
                            size="sm"
                            variant="destructive"
                            onClick={() => handleOpenResponse(order, 'unavailable')}
                          >
                            Non dispo
                          </Button>
                        </>
                      )}
                      
                      {['available', 'partial'].includes(order.status) && (
                        <Button 
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700"
                          onClick={() => handleMarkReady(order.id)}
                        >
                          Marquer prêt
                        </Button>
                      )}
                      
                      {order.status === 'ready' && (
                        <Button 
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleCompletePayment(order.id)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Paiement reçu
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Response Modal */}
      <Dialog open={showResponseModal} onOpenChange={setShowResponseModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {responseData.status === 'processing' && 'Traiter la commande'}
              {responseData.status === 'available' && 'Confirmer disponibilité'}
              {responseData.status === 'partial' && 'Disponibilité partielle'}
              {responseData.status === 'unavailable' && 'Non disponible'}
            </DialogTitle>
            <DialogDescription>
              {selectedOrder && `Commande de ${selectedOrder.patient_name}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {responseData.status !== 'processing' && (
              <div>
                <label className="text-sm font-medium text-slate-700">Prix total (FCFA)</label>
                <Input
                  type="number"
                  placeholder="Ex: 15000"
                  value={responseData.total_price}
                  onChange={(e) => setResponseData({...responseData, total_price: e.target.value})}
                  className="mt-1"
                />
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-slate-700">Message au patient</label>
              <Textarea
                placeholder={
                  responseData.status === 'processing' 
                    ? 'Ex: Nous traitons votre ordonnance...'
                    : responseData.status === 'available'
                    ? 'Ex: Tous vos médicaments sont disponibles'
                    : responseData.status === 'partial'
                    ? 'Ex: Certains médicaments ne sont pas en stock'
                    : 'Ex: Désolé, ces médicaments ne sont pas disponibles'
                }
                value={responseData.pharmacy_response}
                onChange={(e) => setResponseData({...responseData, pharmacy_response: e.target.value})}
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResponseModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSubmitResponse}
              disabled={submitting}
              className={
                responseData.status === 'unavailable' 
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
              }
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                'Confirmer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PharmacyDashboard;
